import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css'; // Ensure Bootstrap CSS is imported

const AIInternshipJourney = () => {
  // Modal and form state
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    resume: null,
    qualification: '',
    currentDesignation: '',
    currentInstitution: '',
    briefSummary: '',
    areaOfInterest: '',
    aboutYou: ''
  });

  // Responsive styles hook
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Modal handlers
  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    // Reset form data on close
    setFormData({
      name: '',
      email: '',
      phone: '',
      resume: null,
      qualification: '',
      currentDesignation: '',
      currentInstitution: '',
      briefSummary: '',
      areaOfInterest: '',
      aboutYou: ''
    });
  };

  // Input change handler with file validation
  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'resume' && files && files[0]) {
      const file = files[0];
      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

      if (file.size > MAX_FILE_SIZE) {
        alert('File size exceeds 10MB. Please choose a smaller file.');
        e.target.value = null; // Clear the invalid file
        setFormData(prev => ({ ...prev, resume: null }));
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };
  
  // Helper function to convert a file to a Base64 string
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]); // Get only the Base64 part
    reader.onerror = error => reject(error);
  });

  // Form submission handler to post data to Google Apps Script
  const handleSubmit = async (e) => {
    e.preventDefault();
    const requiredFields = ['name', 'email', 'resume', 'qualification', 'currentDesignation', 'currentInstitution', 'briefSummary', 'areaOfInterest', 'aboutYou'];
    const isFormValid = requiredFields.every(field => formData[field]);

    if (!isFormValid) {
        alert("Please fill out all required fields marked with *.");
        return;
    }
    
    setIsSubmitting(true);

    // Your Google Apps Script Web App URL
    const GOOGLE_SCRIPT_ENDPOINT = 'https://script.google.com/macros/s/AKfycbxy5bdzpGH_iuiTniDfgmAq4FFNLRxnztUAm7jUzdWX-Xlvgje_oYHYHPnzU-6QXBi_/exec';

    try {
      const base64File = await fileToBase64(formData.resume);
      
      const submissionData = {
        ...formData,
        resume: {
          name: formData.resume.name,
          mimeType: formData.resume.type,
          data: base64File,
        },
      };

      const response = await fetch(GOOGLE_SCRIPT_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors', // Important for cross-origin requests to Apps Script
        body: JSON.stringify(submissionData),
        headers: {
          'Content-Type': 'text/plain;charset=utf-8',
        },
      });

      // Since 'no-cors' mode prevents reading the response, we assume success and show a generic message.
      alert("Thank you for your application! We'll get back to you shortly.");
      handleCloseModal();

    } catch (error) {
      console.error('Submission failed:', error);
      alert(`An error occurred while submitting your application: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Component styles (same as your original component)
  const styles = {
    container: {
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '40px 15px' : '60px 20px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif', backgroundColor: '#f8f9fa', color: '#333',
    },
    contentWrapper: {
      display: 'flex', flexDirection: isMobile ? 'column' : 'row', maxWidth: '1300px', width: '100%', gap: isMobile ? '40px' : '80px', alignItems: 'center', justifyContent: 'center',
    },
    leftSection: {
      flex: isMobile ? 'none' : 1, maxWidth: isMobile ? '100%' : '650px', paddingRight: isMobile ? 0 : '20px', textAlign: isMobile ? 'center' : 'left',
    },
    mainTitle: {
      fontSize: isMobile ? '2.8rem' : '4rem', fontWeight: 800, lineHeight: 1.1, marginBottom: '28px', color: '#1a1a1a'
    },
    highlight: {
      color: '#4f7cff', background: 'linear-gradient(135deg, #4f7cff 0%, #3d63cc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
    },
    description: {
      fontSize: '1.2rem', lineHeight: 1.7, color: '#666', marginBottom: '36px', maxWidth: '580px', marginLeft: isMobile ? 'auto' : 0, marginRight: isMobile ? 'auto' : 0
    },
    featuresList: {
      marginBottom: '44px', textAlign: isMobile ? 'center' : 'left'
    },
    featureItem: {
      display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'center' : 'flex-start', marginBottom: '18px', fontSize: '1.05rem', color: '#555', padding: '4px 0'
    },
    checkmark: {
      width: '24px', height: '24px', backgroundColor: '#4f7cff', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '16px', fontSize: '14px', fontWeight: 'bold', flexShrink: 0
    },
    buttons: {
      display: 'flex', justifyContent: isMobile ? 'center' : 'flex-start', gap: '20px', flexWrap: 'wrap', marginTop: '8px'
    },
    submitBtn: {
      backgroundColor: '#4f7cff', color: 'white', border: 'none', padding: '18px 36px', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 4px 15px rgba(79, 124, 255, 0.3)', minWidth: '180px', outline: 'none'
    },
    rightSection: {
      flex: isMobile ? 'none' : '0 0 420px', display: 'flex', flexDirection: 'column', gap: '28px', alignItems: 'center', width: isMobile ? '100%' : 'auto'
    },
    programImpact: {
      backgroundColor: 'white', padding: '40px 36px', borderRadius: '20px', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)', width: '100%', border: '1px solid rgba(79, 124, 255, 0.1)'
    },
    programImpactTitle: {
      fontSize: '1.4rem', fontWeight: 700, marginBottom: '32px', color: '#1a1a1a', textAlign: 'center', letterSpacing: '-0.5px'
    },
    statsContainer: {
      display: 'grid', gridTemplateColumns: isMobile ? 'repeat(1, 1fr)' : 'repeat(3, 1fr)', gap: '24px', alignItems: 'center'
    },
    statItem: {
      textAlign: 'center', padding: '16px 8px', borderRadius: '12px', transition: 'transform 0.2s ease', cursor: 'default'
    },
    statIcon: {
      width: '56px', height: '56px', borderRadius: '50%', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    },
    statIconGraduates: { backgroundColor: '#4f7cff', color: 'white' },
    statIconSuccess: { backgroundColor: '#22c55e', color: 'white' },
    statIconWeeks: { backgroundColor: '#f59e0b', color: 'white' },
    statIconDot: { width: '28px', height: '28px', borderRadius: '50%', backgroundColor: 'rgba(255, 255, 255, 0.9)' },
    statNumber: { fontSize: '2.2rem', fontWeight: 800, color: '#1a1a1a', marginBottom: '8px', lineHeight: 1 },
    statLabel: { fontSize: '0.9rem', color: '#666', lineHeight: 1.4, fontWeight: 500, letterSpacing: '0.2px', whiteSpace: 'pre-line' },
    deadlineCard: { background: 'linear-gradient(135deg, #4f7cff 0%, #3d63cc 100%)', color: 'white', padding: '36px 32px', borderRadius: '20px', textAlign: 'center', boxShadow: '0 12px 40px rgba(79, 124, 255, 0.4)', width: '100%', position: 'relative', overflow: 'hidden' },
    deadlineCardOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)', pointerEvents: 'none' },
    deadlineHeader: { fontSize: '1.05rem', opacity: 0.9, marginBottom: '12px', fontWeight: 500, position: 'relative', zIndex: 1 },
    deadlineDate: { fontSize: '2.4rem', fontWeight: 800, marginBottom: '12px', position: 'relative', zIndex: 1, letterSpacing: '-1px' },
    deadlineNote: { fontSize: '0.9rem', opacity: 0.85, fontWeight: 500, position: 'relative', zIndex: 1 }
  };

  // Hover handlers (same as your original component)
  const handleSubmitHover = (e) => { e.target.style.backgroundColor = '#3d63cc'; e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 6px 20px rgba(79, 124, 255, 0.4)'; };
  const handleSubmitLeave = (e) => { e.target.style.backgroundColor = '#4f7cff'; e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 15px rgba(79, 124, 255, 0.3)'; };
  const handleStatHover = (e) => { e.currentTarget.style.transform = 'scale(1.05)'; };
  const handleStatLeave = (e) => { e.currentTarget.style.transform = 'scale(1)'; };

  return (
    <>
      <div style={styles.container}>
        <div style={styles.contentWrapper}>
          <div style={styles.leftSection}>
            <h1 style={styles.mainTitle}>
              Begin Your AI <span style={styles.highlight}>Journey</span>
            </h1>

            <p style={styles.description}>
              Join an exclusive cohort of ambitious individuals committed 
              to mastering artificial intelligence and securing career-
              defining internship opportunities.
            </p>

            <div style={styles.featuresList}>
              {[
                'Comprehensive AI curriculum', 'Industry-expert mentorship', 'Real-world project experience',
                'Guaranteed internship opportunities', 'Professional certification', 'Career placement support'
              ].map((feature, idx) => (
                <div key={idx} style={styles.featureItem}>
                  <span style={styles.checkmark}>✓</span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <div style={styles.buttons}>
              <button style={styles.submitBtn} onClick={handleShowModal} onMouseEnter={handleSubmitHover} onMouseLeave={handleSubmitLeave} aria-label="Submit internship application">
                Submit Application
              </button>
            </div>
          </div>

          <div style={styles.rightSection}>
            <div style={styles.programImpact}>
              <h3 style={styles.programImpactTitle}>Program Impact</h3>
              <div style={styles.statsContainer}>
                {[
                  { icon: styles.statIconGraduates, number: '5000+', label: 'Graduates' },
                  { icon: styles.statIconSuccess, number: '95%', label: 'Success' },
                  { icon: styles.statIconWeeks, number: '14', label: 'Weeks' }
                ].map((stat, idx) => (
                  <div key={idx} style={styles.statItem} onMouseEnter={handleStatHover} onMouseLeave={handleStatLeave}>
                    <div style={{...styles.statIcon, ...stat.icon}}>
                      <div style={styles.statIconDot}></div>
                    </div>
                    <div style={styles.statNumber}>{stat.number}</div>
                    <div style={styles.statLabel}>
                      {stat.label.split('\n').map((line, i) => (<div key={i}>{line}</div>))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={styles.deadlineCard} role="region" aria-label="Application deadline information">
              <div style={styles.deadlineCardOverlay}></div>
              <div style={styles.deadlineHeader}>Application Deadline</div>
              <div style={styles.deadlineDate}>Oct 31, 2025</div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Application Form */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Submit Your Internship Application</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            {/* Form Fields - same structure as your original component */}
            <Row>
              <Col md={6} xs={12}>
                <Form.Group className="mb-3" controlId="formName">
                  <Form.Label>Full Name *</Form.Label>
                  <Form.Control type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Your full name" required />
                </Form.Group>
              </Col>
              <Col md={6} xs={12}>
                <Form.Group className="mb-3" controlId="formEmail">
                  <Form.Label>Email Address *</Form.Label>
                  <Form.Control type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="your.email@example.com" required />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6} xs={12}>
                <Form.Group className="mb-3" controlId="formPhone">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+1 (555) 123-4567" />
                </Form.Group>
              </Col>
              <Col md={6} xs={12}>
                <Form.Group className="mb-3" controlId="formResume">
                  <Form.Label>Resume Upload * (PDF, DOC, DOCX; max 10MB)</Form.Label>
                  <Form.Control type="file" accept=".pdf,.doc,.docx" onChange={handleInputChange} name="resume" required />
                  {formData.resume && (<small className="text-muted">Selected file: {formData.resume.name} ({(formData.resume.size / 1024 / 1024).toFixed(2)} MB)</small>)}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6} xs={12}>
                <Form.Group className="mb-3" controlId="formQualification">
                  <Form.Label>Qualification *</Form.Label>
                  <Form.Control type="text" name="qualification" value={formData.qualification} onChange={handleInputChange} placeholder="Highest degree or certification" required />
                </Form.Group>
              </Col>
              <Col md={6} xs={12}>
                <Form.Group className="mb-3" controlId="formDesignation">
                  <Form.Label>Current Designation *</Form.Label>
                  <Form.Select name="currentDesignation" value={formData.currentDesignation} onChange={handleInputChange} required>
                    <option value="">Select your designation</option>
                    <option value="student">Student</option> <option value="intern">Intern</option> <option value="junior-engineer">Junior Engineer</option>
                    <option value="senior-engineer">Senior Engineer</option> <option value="research-assistant">Research Assistant</option> <option value="other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="formInstitution">
              <Form.Label>Current Institution / Organization *</Form.Label>
              <Form.Control type="text" name="currentInstitution" value={formData.currentInstitution} onChange={handleInputChange} placeholder="Your current institution or company" required />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formBriefSummary">
              <Form.Label>Brief Summary *</Form.Label>
              <Form.Control as="textarea" rows={2} name="briefSummary" value={formData.briefSummary} onChange={handleInputChange} placeholder="Brief overview of your background" required />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formAreaOfInterest">
              <Form.Label>Area of Interest *</Form.Label>
              <Form.Control type="text" name="areaOfInterest" value={formData.areaOfInterest} onChange={handleInputChange} placeholder="e.g., Machine Learning, Computer Vision" required />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formAboutYou">
              <Form.Label>About You *</Form.Label>
              <Form.Control as="textarea" rows={4} name="aboutYou" value={formData.aboutYou} onChange={handleInputChange} placeholder="Tell us about yourself, your ambitions, and why this internship matters to you" required />
            </Form.Group>

            <div className="d-flex justify-content-end mt-4">
              <Button variant="secondary" onClick={handleCloseModal} className="me-2" disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AIInternshipJourney;